import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiEdit2, FiTrash2, FiCheck, FiX, FiDroplet } from 'react-icons/fi';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

const DealerColorManager = () => {
  const { dealerId, userRole } = useAuth();
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingColor, setEditingColor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    color_name: '',
    hex_code: '#8B4513',
    color_type: 'any'
  });

  useEffect(() => {
    if (dealerId) loadColors();
  }, [dealerId]);

  const loadColors = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('dealer_colors')
        .select('*')
        .eq('dealer_id', dealerId)
        .order('color_name');
      if (!error && data) setColors(data);
    } catch (err) {
      console.error('Failed to load colors:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.color_name.trim()) {
      alert('Please enter a color name.');
      return;
    }
    if (!form.hex_code.match(/^#[0-9A-Fa-f]{6}$/)) {
      alert('Please enter a valid hex code (e.g. #8B4513).');
      return;
    }

    setSaving(true);
    try {
      if (editingColor) {
        const { error } = await supabase
          .from('dealer_colors')
          .update({
            color_name: form.color_name.trim(),
            hex_code: form.hex_code,
            color_type: form.color_type
          })
          .eq('id', editingColor.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('dealer_colors')
          .insert([{
            dealer_id: dealerId,
            color_name: form.color_name.trim(),
            hex_code: form.hex_code,
            color_type: form.color_type,
            is_active: true
          }]);
        if (error) throw error;
      }

      setShowForm(false);
      setEditingColor(null);
      setForm({ color_name: '', hex_code: '#8B4513', color_type: 'any' });
      await loadColors();
    } catch (err) {
      console.error('Failed to save color:', err);
      alert('Failed to save color: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (color) => {
    setEditingColor(color);
    setForm({
      color_name: color.color_name,
      hex_code: color.hex_code,
      color_type: color.color_type
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this color? It will be removed from future uploads.')) return;
    try {
      const { error } = await supabase
        .from('dealer_colors')
        .delete()
        .eq('id', id);
      if (!error) {
        setColors(colors.filter(c => c.id !== id));
      }
    } catch (err) {
      console.error('Failed to delete color:', err);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingColor(null);
    setForm({ color_name: '', hex_code: '#8B4513', color_type: 'any' });
  };

  const colorTypeLabels = {
    body: 'Body',
    trim: 'Trim',
    roof: 'Roof',
    any: 'All'
  };

  const colorTypeBadge = (type) => {
    const styles = {
      body: 'bg-amber-100 text-amber-700',
      trim: 'bg-blue-100 text-blue-700',
      roof: 'bg-slate-100 text-slate-700',
      any: 'bg-emerald-100 text-emerald-700'
    };
    return styles[type] || styles.any;
  };

  if (!dealerId) {
    return (
      <div className="p-6 text-center text-gray-500">
        <FiDroplet className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p>Color management is available for dealer accounts.</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Custom Colors</h1>
          <p className="text-gray-600">Set up your brand colors once. They appear in every shed upload.</p>
        </div>
        <button
          onClick={() => { setEditingColor(null); setForm({ color_name: '', hex_code: '#8B4513', color_type: 'any' }); setShowForm(true); }}
          className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          <FiPlus className="w-5 h-5" />
          Add Color
        </button>
      </div>

      {/* Add/Edit Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden mb-6"
          >
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingColor ? 'Edit Color' : 'New Custom Color'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                {/* Color Preview + Hex Picker */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={form.hex_code}
                      onChange={(e) => setForm({ ...form, hex_code: e.target.value })}
                      className="w-12 h-10 rounded cursor-pointer border border-gray-200"
                    />
                    <input
                      type="text"
                      value={form.hex_code}
                      onChange={(e) => setForm({ ...form, hex_code: e.target.value })}
                      placeholder="#8B4513"
                      maxLength={7}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 font-mono text-sm"
                    />
                  </div>
                </div>

                {/* Color Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Color Name</label>
                  <input
                    type="text"
                    value={form.color_name}
                    onChange={(e) => setForm({ ...form, color_name: e.target.value })}
                    placeholder="e.g., Heritage Burgundy"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>

                {/* Color Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Used For</label>
                  <select
                    value={form.color_type}
                    onChange={(e) => setForm({ ...form, color_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="any">All (Body, Trim, Roof)</option>
                    <option value="body">Body Only</option>
                    <option value="trim">Trim Only</option>
                    <option value="roof">Roof Only</option>
                  </select>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    {saving ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <FiCheck className="w-4 h-4" />
                    )}
                    {editingColor ? 'Update' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                  >
                    <FiX className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Colors Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl h-24 animate-pulse" />
          ))}
        </div>
      ) : colors.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl shadow-sm">
          <FiDroplet className="w-16 h-16 text-gray-200 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-600 mb-2">No custom colors yet</h3>
          <p className="text-gray-400 mb-6 max-w-md mx-auto">
            Add your branded paint colors here. They'll show up automatically in the body and trim color dropdowns when you upload sheds.
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-lg transition-colors"
          >
            <FiPlus className="w-5 h-5" />
            Add Your First Color
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {colors.map((color) => (
            <motion.div
              key={color.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-4 hover:shadow-md transition-shadow"
            >
              {/* Color Swatch */}
              <div
                className="w-14 h-14 rounded-xl border-2 border-gray-200 flex-shrink-0 shadow-inner"
                style={{ backgroundColor: color.hex_code }}
              />

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-gray-900 truncate">{color.color_name}</h4>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs font-mono text-gray-400">{color.hex_code}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colorTypeBadge(color.color_type)}`}>
                    {colorTypeLabels[color.color_type]}
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-1">
                <button
                  onClick={() => handleEdit(color)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
                >
                  <FiEdit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(color.id)}
                  className="p-2 hover:bg-red-50 rounded-lg transition-colors text-gray-500 hover:text-red-600"
                >
                  <FiTrash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DealerColorManager;
